import { datadogRum } from '@datadog/browser-rum';
import React, { useRef } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  Routes,
  useLocation,
  useResolvedPath,
} from 'react-router-dom';

const joinPath = (base: string, path: string) =>
  path.startsWith('/') ? path : `${base.replace(/\/$/, '')}/${path}`;

/**
 * Drop-in for react-router's `Routes` that names the Datadog RUM view after
 * the matched route pattern (`/users/:userId`), not the URL.
 *
 * Use it for every `Routes`, nested ones included. Datadog's own `Routes` only
 * sees the top-level table, so a nested detail page would be named by its raw
 * URL. A match that ends in a `/*` section is left to that section's nested
 * `TrackedRoutes`.
 */
export const TrackedRoutes: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { pathname } = useLocation();
  const base = useResolvedPath('').pathname;
  const lastPathname = useRef<string | null>(null);

  if (lastPathname.current !== pathname) {
    lastPathname.current = pathname;
    const matches = matchRoutes(
      createRoutesFromChildren(children),
      pathname,
      base
    );
    const last = matches?.[matches.length - 1]?.route.path;
    if (matches && !last?.endsWith('/*')) {
      const viewName = matches.reduce(
        (name, { route }) => (route.path ? joinPath(name, route.path) : name),
        base
      );
      datadogRum.startView(viewName);
    }
  }

  return <Routes>{children}</Routes>;
};
